import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { LaunchControls } from '../../../../components/kyckConnect/LaunchControls';
import { FOLLOW_UP_TOKENS } from '../../../../lib/fitnessBot/followUps';
import { PORTAL_BOT_BOUNDS, validateAssistantName, validateCapabilities, validateFollowUpStep } from '../../../../lib/kyckConnect/portalBot';
import type { BotCapabilities, FollowUpStep } from '../../../../types/leadBot';
import type { PortalSetupResponse } from '../../../../types/kyckConnect';
import { changePortalLaunch, confirmPortalCopied, savePortalAssistant } from './portalSetupApi';

// THE CLUB'S OWN ASSISTANT (club onboarding, Phase 1 C). Launch status and the copied-setup check, then the settings a club may change:
// the assistant's name, the four capability toggles, the follow-ups (wording, wait, on/off), the handover contact and its test numbers.
// ⚠️ NOT HERE and not in the server's schema: the Kyck instructions, caps, the number, vertical, playbook, the reply mode and the
// follow-up send window. The reply mode is SHOWN on a live club ("Only Kyck changes this for now") and has no control.
// ⚠️ The same pure rules the server runs (portalBot.ts) give instant messages; the server stays the authority and its sentence is shown.

const CAP_LABELS: Record<keyof BotCapabilities, string> = {
  discussPromotions: 'Talk about your offers',
  bookAppointments: 'Book tours',
  answerFaqs: 'Answer common questions',
  handleObjections: 'Handle objections',
};

const errText = (e: unknown) => (e instanceof Error ? e.message : 'That did not save.');

function FollowUpRows({ title, steps, onChange, disabled }: { title: string; steps: FollowUpStep[]; onChange: (next: FollowUpStep[]) => void; disabled: boolean }) {
  const set = (i: number, patch: Partial<FollowUpStep>) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-foreground">{title}</h4>
      {steps.map((s, i) => {
        const problem = validateFollowUpStep(s, `Step ${i + 1}`);
        return (
          <div key={i} className="space-y-1 rounded-[14px] border border-border p-3" data-testid={`followup-${title}-${i}`}>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={s.enabled} disabled={disabled} onChange={(e) => set(i, { enabled: e.target.checked })} />
                Step {i + 1} on
              </label>
              <label className="flex items-center gap-2">
                Wait
                <Input type="number" min={1} max={PORTAL_BOT_BOUNDS.followUpDelayMinutes.max / 60} className="w-24" disabled={disabled} value={String(Math.round(s.delayMinutes / 60))} onChange={(e) => set(i, { delayMinutes: Math.round(Number(e.target.value || 0) * 60) })} />
                hours
              </label>
            </div>
            <textarea className="kc-field w-full rounded border border-border bg-background p-2 text-sm text-foreground" rows={2} disabled={disabled} value={s.message} onChange={(e) => set(i, { message: e.target.value })} aria-label={`${title}, step ${i + 1} message`} />
            {problem && <p className="text-xs text-destructive" role="alert">{problem}</p>}
          </div>
        );
      })}
    </div>
  );
}

export function PortalAssistantForm({ setup, editable }: { setup: PortalSetupResponse; editable: boolean }) {
  const qc = useQueryClient();
  const accountId = setup.accountId;
  const ai = setup.aiSms;
  const s = ai?.settings;
  const [name, setName] = useState(s?.assistantName ?? '');
  const [caps, setCaps] = useState<BotCapabilities | null>(s?.capabilities ?? null);
  const [noResponse, setNoResponse] = useState<FollowUpStep[]>(s?.followUps?.noResponse ?? []);
  const [incomplete, setIncomplete] = useState<FollowUpStep[]>(s?.followUps?.incomplete ?? []);
  const [handoff, setHandoff] = useState(s?.handoff ?? { name: '', phone: '', email: '' });
  const [numbers, setNumbers] = useState((s?.testNumbers ?? []).join('\n'));
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['portal-kc'] });
  const move = useMutation({ mutationFn: (to: 'setting_up' | 'testing' | 'live' | 'on_hold') => changePortalLaunch(accountId, to), onSuccess: () => { setLaunchError(null); void refresh(); }, onError: (e) => setLaunchError(errText(e)) });
  const confirm = useMutation({ mutationFn: (b: { sections?: string[]; promotionIds?: string[]; followUps?: boolean }) => confirmPortalCopied(accountId, b), onSuccess: () => { setLaunchError(null); void refresh(); }, onError: (e) => setLaunchError(errText(e)) });
  const save = useMutation({
    mutationFn: () =>
      savePortalAssistant(accountId, {
        assistantName: name,
        ...(caps ? { capabilities: caps } : {}),
        ...(s?.followUps ? { followUps: { noResponse, incomplete } } : {}),
        handoff,
        testNumbers: numbers.split('\n').map((x) => x.trim()).filter(Boolean),
      }),
    onSuccess: () => { setSaveError(null); void refresh(); },
    onError: (e) => setSaveError(errText(e)),
  });

  if (!ai || !s || !caps) return null;
  const nameProblem = name === s.assistantName ? null : validateAssistantName(name);
  const capProblem = validateCapabilities(caps, s.hasBookingCalendar);
  const blocked = nameProblem !== null || capProblem !== null;

  return (
    <div className="space-y-4" data-testid="portal-assistant">
      {setup.launch && (
        <LaunchControls
          clubName={setup.accountName}
          launch={setup.launch}
          actor="client"
          canEdit={editable}
          canChangeReplyMode={false}
          busy={move.isPending || confirm.isPending}
          error={launchError}
          onMove={(to) => move.mutate(to)}
          onReplyMode={() => {}}
          onConfirmReviewed={(b) => confirm.mutate(b)}
        />
      )}

      <div className="kc-card space-y-4 rounded-[18px] border border-border p-4">
        <h3 className="kc-label text-muted-foreground">Your assistant</h3>
        <label className="block text-sm text-foreground">
          <span className="mb-1 block">Assistant name</span>
          <Input value={name} maxLength={PORTAL_BOT_BOUNDS.assistantNameMax} disabled={!editable} className="w-64" onChange={(e) => setName(e.target.value)} aria-label="Assistant name" />
          <span className="text-xs text-muted-foreground">1 to 20 letters and spaces. It appears in the first message.</span>
          {nameProblem && <span className="block text-xs text-destructive" role="alert">{nameProblem}</span>}
        </label>

        <fieldset className="space-y-1" data-testid="portal-capabilities">
          <legend className="text-sm font-medium text-foreground">What the assistant can do</legend>
          {(Object.keys(CAP_LABELS) as Array<keyof BotCapabilities>).map((k) => {
            const needsCalendar = k === 'bookAppointments' && !s.hasBookingCalendar;
            return (
              <label key={k} className="flex flex-wrap items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={caps[k]} disabled={!editable || (needsCalendar && !caps[k])} onChange={(e) => setCaps({ ...caps, [k]: e.target.checked })} />
                {CAP_LABELS[k]}
                {needsCalendar && <span className="text-xs text-muted-foreground">Kyck needs to set up a booking calendar for your club before this can be turned on.</span>}
              </label>
            );
          })}
        </fieldset>

        {s.followUps && (
          <div className="space-y-3" data-testid="portal-followups">
            <h4 className="text-sm font-medium text-foreground">Follow-ups</h4>
            <p className="text-xs text-muted-foreground">
              You can use {FOLLOW_UP_TOKENS.map((t) => `{{${t}}}`).join(' ')}. Follow-ups only go out between 8am and 8pm, and never to someone who has opted out. That is not something you can change.
            </p>
            <FollowUpRows title="Lead never replied" steps={noResponse} onChange={setNoResponse} disabled={!editable} />
            <FollowUpRows title="Lead went quiet" steps={incomplete} onChange={setIncomplete} disabled={!editable} />
          </div>
        )}

        <div className="space-y-2" data-testid="portal-handover">
          <h4 className="text-sm font-medium text-foreground">Who we tell when a lead needs a person</h4>
          <div className="grid gap-2 sm:grid-cols-3">
            <Input placeholder="Name" aria-label="Handover contact name" value={handoff.name} disabled={!editable} onChange={(e) => setHandoff({ ...handoff, name: e.target.value })} />
            <Input placeholder="Mobile" aria-label="Handover contact mobile" value={handoff.phone} disabled={!editable} onChange={(e) => setHandoff({ ...handoff, phone: e.target.value })} />
            <Input placeholder="Email" aria-label="Handover contact email" value={handoff.email} disabled={!editable} onChange={(e) => setHandoff({ ...handoff, email: e.target.value })} />
          </div>
        </div>

        <label className="block text-sm text-foreground" data-testid="portal-test-numbers">
          <span className="mb-1 block">Test numbers (one per line, up to {PORTAL_BOT_BOUNDS.testNumbersMax})</span>
          <textarea className="kc-field w-full max-w-sm rounded border border-border bg-background p-2 text-foreground" rows={3} disabled={!editable} value={numbers} onChange={(e) => setNumbers(e.target.value)} aria-label="Test numbers" />
          <span className="text-xs text-muted-foreground">These phones get real replies while your club is setting up or testing. If one phone has a conversation open with another club that shares your number, close it there first to test here.</span>
        </label>

        {capProblem && <p className="text-xs text-destructive" role="alert">{capProblem}</p>}
        {saveError && <p className="text-sm text-destructive" role="alert" data-testid="assistant-save-error">{saveError}</p>}
        {editable ? (
          <Button disabled={save.isPending || blocked} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save assistant settings'}</Button>
        ) : (
          <p className="text-xs text-muted-foreground">You can see these settings but not change them.</p>
        )}
      </div>
    </div>
  );
}
